import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import {
  CustomerType,
  PrivateCustomerKsvResponse,
  CommercialCustomerKsvResponse,
} from '../../../../../generated/ApiClient';
import { EnhancedTable } from '../../../../../common/table/EnhancedTable';
import { HeadCell } from '../../../../../common/table/EnhancedTableHead';
import { KsvSearchResult } from './useKsvSearch';

interface Props {
  results: KsvSearchResult[];
  customerType: CustomerType;
  onResultSelected: (ksvId: string) => void;
}

interface PrivateRow {
  id: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  address: string;
}

interface CommercialRow {
  id: string;
  companyName: string;
  address: string;
}

export function KsvSearchResultsTable({ results, customerType, onResultSelected }: Props) {
  const { t } = useTranslation();

  const handleRowClick = (_: React.MouseEvent<unknown>, id: string) => {
    onResultSelected(id);
  };

  if (results.length === 0) {
    return null;
  }

  if (customerType === CustomerType.Commercial) {
    const rows: CommercialRow[] = (results as CommercialCustomerKsvResponse[]).map((r) => ({
      id: r.ksvId,
      companyName: r.companyName,
      address: `${r.zip} ${r.city}, ${r.street} ${r.house}`,
    }));

    const headCells: readonly HeadCell<CommercialRow>[] = [
      { id: 'id', disablePadding: false, label: t('customers.ksv.result.commercial.id') },
      { id: 'companyName', disablePadding: false, label: t('customers.ksv.result.commercial.companyName') },
      { id: 'address', disablePadding: false, label: t('customers.ksv.result.commercial.address') },
    ];

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {t('identityCheck.ksv.resultCount', { count: results.length })}
        </Typography>
        <EnhancedTable
          rows={rows}
          headCells={headCells}
          hideCheckbox={true}
          variant="default"
          onRowClick={handleRowClick}
        />
      </Box>
    );
  }

  const rows: PrivateRow[] = (results as PrivateCustomerKsvResponse[]).map((r) => ({
    id: r.ksvId,
    firstName: r.firstName,
    lastName: r.lastName,
    birthDate: r.birthDate?.toLocaleDateString() ?? '',
    address: `${r.zipCode} ${r.city}, ${r.street} ${r.house}`,
  }));

  const headCells: readonly HeadCell<PrivateRow>[] = [
    { id: 'id', disablePadding: false, label: t('customers.ksv.result.private.id') },
    { id: 'firstName', disablePadding: false, label: t('customers.ksv.result.private.firstName') },
    { id: 'lastName', disablePadding: false, label: t('customers.ksv.result.private.lastName') },
    { id: 'birthDate', disablePadding: false, label: t('customers.ksv.result.private.birthDate') },
    { id: 'address', disablePadding: false, label: t('customers.ksv.result.private.address') },
  ];

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary">
        {t('identityCheck.ksv.resultCount', { count: results.length })}
      </Typography>
      <EnhancedTable
        rows={rows}
        headCells={headCells}
        hideCheckbox={true}
        variant="default"
        onRowClick={handleRowClick}
      />
    </Box>
  );
}
